import { useState, useEffect } from 'react';
import { List, type RowComponentProps } from 'react-window';
import { useTranslation } from 'react-i18next';
import Button from '../../widgets/Button';

import {
  MultiLevelBarChartMetaData,
  MultiLevelBarChartTeamNode,
  WidgetDefinition,
} from '../../../shared/types/widget';

import arrowLeft from '../../../assets/icons/General/arrow-left.svg';
import refreshIcon from '../../../assets/icons/General/refresh-icon.svg';
import { TFunction } from 'i18next';

type MultiLevelBarChartProps = {
  definition: WidgetDefinition;
  content: MultiLevelBarChartMetaData;
};

// Props for TreeView component
interface TreeViewProps {
  nodes: MultiLevelBarChartTeamNode[];
  onSelect: (node: MultiLevelBarChartTeamNode) => void;
  // Height of chart view
  height?: number;
  // Height of each row
  itemSize?: number;
  bgColor: string;
}

const Row = ({
  index,
  style,
  filteredNodes,
  maxValue,
  bgColor,
  onSelect,
}: RowComponentProps<{
  filteredNodes: MultiLevelBarChartTeamNode[];
  maxValue: number;
  bgColor: string;
  onSelect: (node: MultiLevelBarChartTeamNode) => void;
}>) => {
  const node = filteredNodes[index];
  const relativeWidth = (node.value / maxValue) * 100;
  const hasValidChildren = node.children.some(
    (c: MultiLevelBarChartTeamNode) => c.value > 0,
  );

  return (
    <button
      type="button"
      className={
        'box-border overflow-hidden text-left text-ellipsis whitespace-nowrap' +
        (hasValidChildren ? ' cursor-pointer' : ' cursor-default')
      }
      style={style}
      onClick={() => {
        if (hasValidChildren) onSelect(node);
      }}
      title={`${node.name} (${node.value})`}
    >
      <div className="text-grey-800 mb-1 flex flex-row items-center text-xs">
        {node.name}
        {hasValidChildren && (
          <img src={arrowLeft} className="relative left-2 rotate-180" alt="" />
        )}
      </div>
      <div className="flex gap-x-[22px]">
        <div
          className="px-3 py-[6px]"
          style={{
            width: `${relativeWidth}%`,
            backgroundColor: bgColor ?? '#1570EF',
          }}
        />
        <span className="text-grey-900 text-sm font-medium">{node.value}</span>
      </div>
    </button>
  );
};

const TreeView = ({
  nodes,
  onSelect,
  height = 366,
  itemSize = 60,
  bgColor,
}: TreeViewProps) => {
  if (!Array.isArray(nodes)) return null;

  // Remove nodes with 0 value
  const filteredNodes = nodes.filter((n) => n.value > 0);

  const maxValue = Math.max(...filteredNodes.map((n) => n.value), 1);

  return (
    <div className="pr-10 pb-6 pl-6">
      <List
        rowComponent={Row}
        rowCount={filteredNodes.length}
        rowHeight={itemSize}
        rowProps={{
          filteredNodes,
          maxValue,
          bgColor,
          onSelect,
        }}
      />
    </div>
  );
};

const ChartBreadcrumb = ({
  t,
  path,
  onNavigate,
}: {
  t: TFunction;
  path: MultiLevelBarChartTeamNode[];
  onNavigate: (index: number) => void;
}) => {
  return (
    <div className="flex min-w-0 flex-wrap items-center">
      <span className="pr-1 text-sm font-semibold"> {t('BREAKDOWN')}:</span>
      {path.map(({ id, name }, i) => {
        const isLast = i === path.length - 1;

        return (
          <div
            key={id}
            className="flex items-center truncate text-sm font-semibold text-[#1463AB]"
          >
            {i > 0 && <span className="px-1">{'>'}</span>}
            {isLast ? (
              <span>{name}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(i)}
                className="cursor-pointer"
              >
                {name}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

function MultiLevelBarChart({ definition, content }: MultiLevelBarChartProps) {
  const { meta } = content || {};

  const [chartPath, setChartPath] = useState<MultiLevelBarChartTeamNode[]>([]);
  const { t } = useTranslation();

  const rootNodes = Array.isArray(content.data) ? content.data : [];
  const currentNodes =
    chartPath.length === 0
      ? rootNodes
      : (chartPath[chartPath.length - 1]?.children ?? []);

  // Adds the selected node to the stack only if it has children
  const handleNodeExpand = (node: MultiLevelBarChartTeamNode) => {
    if (node.children.length > 0) {
      setChartPath((prev) => [...prev, node]);
    }
  };

  return (
    <div className="w-full">
      <div className="intl-overflow-widget overflow-auto rounded-2xl">
        <div className="min-w-[550px]">
          {chartPath.length > 0 && (
            <div className="mb-6 flex flex-col px-6">
              <div className="flex items-start gap-x-2 lg:items-center">
                <button
                  onClick={() => setChartPath((prev) => prev.slice(0, -1))}
                  className="relative -top-1 cursor-pointer lg:top-0"
                >
                  <img src={arrowLeft} alt="" />
                </button>
                <div className="grid w-full items-center justify-between gap-y-4 lg:flex">
                  <ChartBreadcrumb
                    t={t}
                    path={chartPath}
                    onNavigate={(index) =>
                      setChartPath(chartPath.slice(0, index + 1))
                    }
                  />

                  <Button
                    variant="outline"
                    className="flex min-w-fit items-center gap-x-2 !font-medium"
                    onClick={() => setChartPath([])}
                  >
                    <img src={refreshIcon} alt="" /> {t('RESET_SELECTION')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <TreeView
            nodes={currentNodes}
            onSelect={handleNodeExpand}
            bgColor={meta?.backgroundColors}
          />
        </div>
      </div>
    </div>
  );
}

export default MultiLevelBarChart;
